import React from 'react';
import { PendingRegisteredOffer } from '@bb/domain/dist/types';
import { PDFDocumentProxy, PDFPageProxy, RenderTask } from '../pdfjs';
import Loading from '../react-common/Loading';
import Empty from '../react-common/Empty';
import Form801InfoPane, {
  Offer as Form801InfoPaneOffer,
} from './Form801InfoPane';

export interface Offer extends Form801InfoPaneOffer {
  id: string;
  attachmentForm801?: { filename: string; url: string };
}

interface PdfPreviewProps {
  documentPath: string;
  offer: Offer | PendingRegisteredOffer;
}

interface RenderTasks {
  [key: string]: RenderTask;
}

interface PdfPreviewState {
  pdf?: PDFDocumentProxy;
  currentPage: number;
  pages: PDFPageProxy[];
  zoom: number;
  currentPageContainerWidth: number;
  isCurrentPageLarge: boolean;
  renderTasks: RenderTasks;
  isPdf: boolean;
  isImage: boolean;
  invalidImage: boolean;
}

interface RenderPageOptions {
  size?: 'sm' | 'lg';
  zoom?: number;
}

const PAGE_SCALES = {
  sm: 0.25,
  lg: 1,
};

const ZOOM_SCALES = [0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 3];

class PdfPreview extends React.Component<PdfPreviewProps, PdfPreviewState> {
  private currentPageContainer?: Element;

  private currentPageCanvas?: HTMLCanvasElement;

  private modalImageElement?: HTMLImageElement;

  private pdfPreviewFullscreen?: HTMLDivElement;

  constructor(props: PdfPreviewProps) {
    super(props);

    const normalizedFilename =
      props.offer.attachmentForm801.filename.toLowerCase();

    this.state = {
      currentPage: 1,
      pages: [],
      zoom: 3,
      currentPageContainerWidth: 0,
      isCurrentPageLarge: false,
      renderTasks: {},
      invalidImage: false,
      isPdf: normalizedFilename.endsWith('.pdf'),
      isImage:
        normalizedFilename.endsWith('.png') ||
        normalizedFilename.endsWith('.jpg') ||
        normalizedFilename.endsWith('.jpeg'),
    };
  }

  async componentDidMount() {
    let invalidImage = false;
    if (this.state.isPdf) {
      // Handle pdf attachment
      const { currentPage } = this.state;
      const pdf = await this.getPdf(this.props.documentPath);
      await this.getPages(pdf);
      const pageNumbers = this.getPageNumbers(pdf);

      this.renderCurrentPage(currentPage);
      if (pageNumbers.length > 1) this.renderPageList(pageNumbers);
    } else if (this.state.isImage) {
      // Handle image attachment
      await this.renderImage();
    } else {
      try {
        await this.renderImage();
      } catch (err) {
        invalidImage = true;
      }
    }

    const width = this.currentPageContainer.clientWidth;
    const isCurrentPageLarge = this.currentPageCanvas.width > width;
    this.setState({
      currentPageContainerWidth: width,
      isCurrentPageLarge,
      invalidImage,
    });
  }

  componentDidUpdate() {
    // Update width calculations on page resize
    if (this.currentPageContainer && this.currentPageCanvas) {
      const width = this.currentPageContainer.clientWidth;
      if (width !== this.state.currentPageContainerWidth) {
        const isCurrentPageLarge = this.currentPageCanvas.width > width;
        this.setState({ currentPageContainerWidth: width, isCurrentPageLarge });
      }
    }
  }

  getPdf = async (documentPath: string = this.props.documentPath) => {
    // @ts-ignore
    const pdfjsLib = await import('pdfjs-dist/build/pdf');
    // @ts-ignore
    const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
    const loadingTask = pdfjsLib.getDocument({
      url: documentPath,
      isEvalSupported: false,
    });
    const pdf = await loadingTask.promise;
    this.setState({ pdf });
    return pdf;
  };

  getPageNumbers = (pdf: PDFDocumentProxy = this.state.pdf) =>
    [...Array(pdf.numPages).keys()].map(key => key + 1);

  getPages = async (pdf: PDFDocumentProxy = this.state.pdf) => {
    const pageNumbers = this.getPageNumbers(pdf);
    const pages = await Promise.all(
      // The redundant `async` is to silence a type error about incompatible
      // `then`s from PDF.js; their `then` implementation seems to be compatible
      // with async but not Promises
      pageNumbers.map(async pageNumber => pdf.getPage(pageNumber)),
    );
    this.setState({ pages });
    return pages;
  };

  renderImage = async (zoom: number = this.state.zoom) => {
    const canvas = this.currentPageCanvas;
    const context = canvas.getContext('2d');

    const scale = ZOOM_SCALES[zoom];

    const img = new Image();
    img.onload = () => {
      const [width, height] = [
        img.naturalWidth * scale,
        img.naturalHeight * scale,
      ];
      canvas.width = width;
      canvas.height = height;
      context.drawImage(img, 0, 0, width, height);
    };
    img.src = this.props.offer.attachmentForm801.url;
  };

  renderPage = async (
    pageNumber: number,
    canvasId: string,
    options: RenderPageOptions = {},
  ) => {
    // Don't render if it's already rendering
    if (this.state.renderTasks[canvasId]) return;

    const page = this.state.pages[pageNumber - 1];

    const { size = 'lg', zoom = 3 } = options;

    const scale = PAGE_SCALES[size] * ZOOM_SCALES[zoom];
    const viewport = page.getViewport({ scale });

    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    this.setState({
      isCurrentPageLarge: canvas.width > this.state.currentPageContainerWidth,
    });

    const renderContext = {
      canvasContext: context,
      viewport,
    };
    const renderTask = page.render(renderContext);
    this.setState({
      renderTasks: { ...this.state.renderTasks, [canvasId]: renderTask },
    });
    return renderTask.promise.then(() => {
      this.setState({
        renderTasks: { ...this.state.renderTasks, [canvasId]: null },
      });
    });
  };

  renderCurrentPage = async (
    page: number = this.state.currentPage,
    zoom: number = this.state.zoom,
  ) => this.renderPage(page, 'current-page-canvas', {
      zoom,
    });

  renderPageList = async (pageNumbers: number[] = this.getPageNumbers()) => {
    await Promise.all(
      // eslint-disable-next-line array-callback-return
      pageNumbers.map(pageNumber => {
        this.renderPage(pageNumber, `page-list-canvas-${pageNumber}`, {
          size: 'sm',
        });
      }),
    );
  };

  changePage = (pageNumber: number) => {
    this.setState({ currentPage: pageNumber });
    this.renderCurrentPage(pageNumber);
  };

  zoomIn = () => {
    const { zoom, isPdf, isImage, invalidImage, currentPage } = this.state;
    if (zoom >= ZOOM_SCALES.length - 1) return;

    const newZoom = zoom + 1;
    this.setState({ zoom: newZoom });
    if (isPdf) {
      this.renderCurrentPage(currentPage, newZoom);
    } else if (isImage || !invalidImage) {
      this.renderImage(newZoom);
    }
  };

  openImagePDF = () => {
    const { isPdf, isImage, currentPage, invalidImage } = this.state;
    const { offer } = this.props;
    // if it's invalidImage, this function should never be called, this check is just in case
    if (window.innerWidth > 767 || invalidImage) {
      return;
    }

    this.pdfPreviewFullscreen.style.display = 'block';
    if (isPdf) {
      this.renderPage(currentPage, 'full-screen-page-canvas', {});
    }
    if (isImage || !invalidImage) {
      this.modalImageElement.src = offer.attachmentForm801.url;
    }
  };

  closeImagePDF = () => {
    this.pdfPreviewFullscreen.style.display = 'none';
  };

  zoomOut = () => {
    const { zoom, isPdf, isImage, invalidImage, currentPage } = this.state;
    if (zoom <= 0) return;

    const newZoom = zoom - 1;
    this.setState({ zoom: newZoom });
    if (isPdf) {
      this.renderCurrentPage(currentPage, newZoom);
    } else if (isImage || !invalidImage) {
      this.renderImage(newZoom);
    }
  };

  onNextPage = () => {
    const { currentPage, pages } = this.state;
    if (currentPage >= pages.length) {
      return;
    }
    this.changePage(currentPage + 1);
  };

  onPrevPage = () => {
    const { currentPage } = this.state;
    if (currentPage <= 1) {
      return;
    }
    this.changePage(currentPage - 1);
  };

  render() {
    const { invalidImage } = this.state;
    if (this.state.isPdf && !this.state.pdf) return <Loading size="fa-4x" />;

    return (
      <div className="pdf-preview">
        {this.state.pdf && this.state.pages.length > 1 && (
          <ul className="pdf-preview__page-list hidden-xs">
            {this.getPageNumbers().map(pageNumber => (
              <li
                key={`page-${pageNumber}`}
                className={`${
                  this.state.currentPage === pageNumber ? 'is-current-page' : ''
                }`}
              >
                <button
                  className="btn btn-link"
                  onClick={() => this.changePage(pageNumber)}
                >
                  <canvas id={`page-list-canvas-${pageNumber}`} />
                  <div className="pdf-preview__page-list-number">
                    {pageNumber}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div
          data-cy="form-801-modal-pdf-preview"
          className={`pdf-preview__current-page ${
            this.state.isCurrentPageLarge ? 'large-page' : ''
          }`}
          ref={e => {
            this.currentPageContainer = e;
          }}
        >
          <div className="pdf-nav-bar">
            <div className="pdf-preview__zoom-control">
              <button onClick={this.zoomOut}>
                <i className="fa fa-minus" />
              </button>
              <button onClick={this.zoomIn}>
                <i className="fa fa-plus" />
              </button>
            </div>
            <div className="pdf-preview__page-control">
              <button onClick={this.onPrevPage}>
                <i className="fa fa-arrow-left"></i>
              </button>
              <button onClick={this.onNextPage}>
                <i className="fa fa-arrow-right"></i>
              </button>
            </div>
          </div>
          <div
            ref={e => {
              this.pdfPreviewFullscreen = e;
            }}
            className="pdf-preview-fullscreen"
            onClick={this.closeImagePDF}
          >
            <canvas id="full-screen-page-canvas"></canvas>
            <img
              className="img-content"
              ref={e => {
                this.modalImageElement = e;
              }}
            ></img>
          </div>

          {invalidImage ? (
            <Empty text="Offer Doc failed to load" />
          ) : (
            <canvas
              id="current-page-canvas"
              onClick={this.openImagePDF}
              ref={e => {
                this.currentPageCanvas = e;
              }}
            />
          )}
        </div>

        <Form801InfoPane
          data-cy="form-801-modal-info-pane"
          offer={this.props.offer}
        />
      </div>
    );
  }
}

export default PdfPreview;
